import React, { useState, useMemo, useCallback, useEffect } from 'react';
import styled from 'styled-components';

import MonitorService from 'services/monitor';

import useInterval from "hooks/useInterval";

import Animate, { FadeIn } from 'components/Animate';
import { Row, Col } from 'components/layout';

import Board from '../components/MonitorBoard';
import DcStats from '../components/DcStats';
import LiveViewCameras from '../components/LiveViewCameras';

import { filterDoorsAndSummary } from '../helpers';

const DATA_UPDATE_INTERVAL = 3 * 60 * 1000;
const ALERTS_UPDATE_INTERVAL = 60 * 1000;

const DoorsMonitor = ({ hardwareId, setMonitorInfo }) => {
  const monitorService = useMemo(() => new MonitorService(), []);

  const [doorsStats, setDoorsStats] = useState({});
  const [alerts, setAlerts] = useState({ data: [], triggered: 0 });

  const loadingBoardData = useMemo(() => {
    const { doors, summary_loading } = doorsStats;
    return filterDoorsAndSummary(doors, summary_loading, 'LOAD');
  }, [doorsStats]);

  const unloadingBoardData = useMemo(() => {
    const { doors, summary_unloading } = doorsStats;
    return filterDoorsAndSummary(doors, summary_unloading, 'UNLOAD');
  }, [doorsStats]);

  const activeAlerts = useMemo(() => {
    return alerts.data.slice(alerts.triggered);
  }, [alerts]);

  const getDoorStats = useCallback(async () => {
    const doorStats = await monitorService.getLiveDoors({ hw_id: hardwareId });
    if (!doorStats) {
      return;
    }

    setDoorsStats(doorStats);

    if (doorStats.doors.length > 0) {
      const { location_name, timezone_offset } = doorStats.doors[0];
      setMonitorInfo({
        locationName: location_name,
        tzOffset: timezone_offset
      });
    }
  }, [monitorService, hardwareId, setMonitorInfo]);

  const getAlerts = useCallback(async () => {
    const alerts = await monitorService.getAlerts({ hw_id: hardwareId });
    if (!alerts) {
      return;
    }

    const now = Date.now();
    const newAlerts = alerts.filter(n => {
      return Date.parse(n.date_created) >= now - (ALERTS_UPDATE_INTERVAL);
    }).map(n => n.door_id);

    setAlerts(alerts => ({
      data: [...alerts.data, ...newAlerts],
      triggered: alerts.data.length,
    }));
  }, [monitorService, hardwareId]);

  useEffect(() => {
    getDoorStats();
  }, [getDoorStats]);

  useEffect(() => {
    getAlerts();
  }, [getAlerts]);

  useInterval(getDoorStats, DATA_UPDATE_INTERVAL);

  useInterval(getAlerts, ALERTS_UPDATE_INTERVAL, [monitorService]);

  return (
    <Animate animation={FadeIn} duration="1s" timingFunction="linear" style={{ height: '100%' }}>
      <Row style={{ height: '100%' }}>
        <Col span={5}>
          <SideBarWrapper>
            <DcStats summary={doorsStats.summary} style={{ paddingBottom: '3.6vh' }} />
            {doorsStats.doors && <LiveViewCameras
              doors={doorsStats.doors}
              doorsView={doorsStats.screenshots}
            />}
          </SideBarWrapper>
        </Col>
        <Col span={19} style={{ borderLeft: '1px solid #DAE0E6', padding: '1.64vw' }}>
          <Row style={{ minHeight: '50%', paddingBottom: '4%' }}>
            <Col span={24}>
              <Board title="Inbound" data={unloadingBoardData} alerts={activeAlerts} />
            </Col>
          </Row>
          <Row style={{ minHeight: '50%' }}>
            <Col span={24}>
              <Board title="Outbound" data={loadingBoardData} alerts={activeAlerts} />
            </Col>
          </Row>
        </Col>
      </Row>
    </Animate>
  );
};

const SideBarWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  background-color: #EFF2F5;
`;

export default DoorsMonitor;